import { useNavigation } from "@react-navigation/native"
import { StackNavigationProp } from "@react-navigation/stack"
import { Button, generateShadow, Subtitle1, useTheme, useUser, VectorIcon } from "capsule"
import React, { FC, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { StyleSheet, View } from "react-native"
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view"

import CodeView from "../../components/CodeView"
import { PractitionerHomeNS } from "../../features/i18n/constants"
import { ICodesParamList } from "../MainScreen/MainTabs"

type NavigationProp = StackNavigationProp<ICodesParamList>

const CodesScreen: FC = () => {
  const navigation = useNavigation<NavigationProp>()
  const {
    colors: {
      primary,
      surface: { background },
      white: { highEmphasis: white },
    },
    dimensions: { spacing },
    screenStyle,
  } = useTheme()
  const { t } = useTranslation(PractitionerHomeNS)
  const { userData } = useUser()
  const isSurgeon = userData?.role === "surgeon"

  const onPrescriptionLCAPress = useCallback(() => {
    navigation.navigate("PrescriptionLCAEligibility")
  }, [navigation])

  const lcaIcon = useMemo(
    () => () => <VectorIcon color={white} category="MaterialIcons" name="medical-services" />,
    [white],
  )

  const lcaIconTitle = useMemo(
    () => () =>
      <VectorIcon size={50} color={primary} category="MaterialIcons" name="medical-services" />,
    [primary],
  )

  const s = useMemo(
    () => ({
      scrollView: [
        styles.scrollView,
        {
          backgroundColor: background,
        },
      ],
      contentScrollView: {
        paddingTop: spacing,
        paddingBottom: spacing * 4,
      },
      separator: {
        paddingVertical: spacing,
      },
      lcaSection: [
        styles.lcaSection,
        {
          padding: spacing,
          backgroundColor: white,
          ...generateShadow(1),
        },
      ],
      lcaSubtitle: [
        styles.lcaSubtitle,
        {
          paddingVertical: spacing,
        },
      ],
      lcaButton: styles.lcaButton,
    }),
    [background, spacing, white],
  )

  return (
    <View style={s.scrollView}>
      <KeyboardAwareScrollView
        style={screenStyle}
        enableOnAndroid
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={s.contentScrollView}
      >
        {isSurgeon ? (
          <>
            <View style={s.lcaSection}>
              <View>{lcaIconTitle()}</View>
              <Subtitle1 emphasis="high" style={s.lcaSubtitle}>
                {t("subtitle.lca")}
              </Subtitle1>
              <Button icon={lcaIcon} style={s.lcaButton} onPress={onPrescriptionLCAPress}>
                {t("button.prescriptionLCA")}
              </Button>
            </View>
            <View style={s.separator} />
          </>
        ) : null}
        <CodeView subtitle={t("subtitle.activate")} name="new_code" mode="activate_pack" />
        <View style={s.separator} />
        <CodeView subtitle={t("subtitle.order")} name="order_code" order mode="order" />
      </KeyboardAwareScrollView>
    </View>
  )
}

const styles = StyleSheet.create({
  scrollView: {
    height: "100%",
  },
  lcaSection: {
    alignItems: "center",
  },
  lcaSubtitle: {
    textAlign: "center",
  },
  lcaButton: {
    width: "100%",
  },
})

export default CodesScreen
